import axios, { AxiosError, AxiosResponse } from "axios";
import { useMemo, useState } from "react";
import { ValidateAddressRequest, ValidateAddressResponse } from "types/addressValidation";
import { useAlert } from "./alerts/useAlert";
import { ValidateAddressErrorResponse } from "../types/addressValidation";

const googleMapsApiKey: string = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
const apiUrl: string = `https://addressvalidation.googleapis.com/v1:validateAddress?key=${googleMapsApiKey}`;
const skipResponse = { result: { verdict: { possibleNextAction: "SKIP" } } } as ValidateAddressResponse;

const useAddressValidation = () => {
  const { alertError } = useAlert();
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState<ValidateAddressResponse[]>([]);
  const lastResponse = useMemo(() => responses[responses.length - 1], [responses]);

  const request = async (address: ValidateAddressRequest) => {
    setLoading(true);

    return await axios
      .post(apiUrl, address)
      .then((response: AxiosResponse<ValidateAddressResponse>) => setResponses((value) => [...value, response.data]))
      .catch((error: AxiosError | Error) => {
        if (error instanceof AxiosError) {
          const { response } = error;
          if (response && response.status === 400) handleErrorResponse(response.data);
          else alertError(`${error.message}: ${error.code}`);
        } else alertError(error.message || "Couldn't validate the address.");
      })
      .finally(() => setLoading(false));
  };

  const handleErrorResponse = (response: ValidateAddressErrorResponse) => {
    const { status, message } = response.error;
    if (status === "INVALID_ARGUMENT" && message.includes("Unsupported region code"))
      // We can expect this 400 error; not all regions are supported under address validation API.
      // In this case, we will insert a response with a SKIP action so the validation flow continues uninterrupted.
      setResponses((value) => [...value, skipResponse]);
  };

  return { request, loading, responses, lastResponse };
};

export default useAddressValidation;
